import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/use-auth';
import { ROUTES } from '@/config/constants';
import type { AppRole } from '@/config/constants';
import { getDefaultAuthenticatedRoute } from '@/features/auth/auth-routing';
import { Button } from '@/components/ui/button';
import { AuthLoadingScreen } from '@/components/shared/AuthLoadingScreen';

interface ProtectedRouteProps {
  children?: ReactNode;
  requiredRole?: AppRole;
  requireClientAccess?: boolean;
  platformOnly?: boolean;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requireClientAccess = false,
  platformOnly = false,
}: ProtectedRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    authLoadError,
    activeRole,
    hasClientAccess,
    isPlatformAdmin,
    signOut,
  } = useAuth();
  const location = useLocation();
  const defaultRoute = getDefaultAuthenticatedRoute({ hasClientAccess, isPlatformAdmin });

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  if (authLoadError) {
    return <AccessContextError message={authLoadError} onSignOut={signOut} />;
  }

  if (platformOnly && !isPlatformAdmin) {
    return <Navigate to={defaultRoute} replace />;
  }

  if (requireClientAccess && !hasClientAccess) {
    return <Navigate to={defaultRoute} replace />;
  }

  if (requiredRole && activeRole !== requiredRole) {
    return <Navigate to={defaultRoute} replace />;
  }

  return children ? <>{children}</> : <Outlet />;
}

export function PlatformRoute({ children }: { children?: ReactNode }) {
  return (
    <ProtectedRoute platformOnly>
      {children}
    </ProtectedRoute>
  );
}

export function DefaultAuthenticatedRoute() {
  const { isAuthenticated, isLoading, authLoadError, hasClientAccess, isPlatformAdmin, signOut } = useAuth();

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (authLoadError) {
    return <AccessContextError message={authLoadError} onSignOut={signOut} />;
  }

  return (
    <Navigate
      to={getDefaultAuthenticatedRoute({ hasClientAccess, isPlatformAdmin })}
      replace
    />
  );
}

interface PublicOnlyRouteProps {
  children: ReactNode;
}

export function PublicOnlyRoute({ children }: PublicOnlyRouteProps) {
  const {
    isAuthenticated,
    isLoading,
    authLoadError,
    hasClientAccess,
    isPlatformAdmin,
    signOut,
  } = useAuth();

  if (isLoading) {
    return <AuthLoadingScreen />;
  }

  if (isAuthenticated) {
    if (authLoadError) {
      return <AccessContextError message={authLoadError} onSignOut={signOut} />;
    }

    return (
      <Navigate
        to={getDefaultAuthenticatedRoute({ hasClientAccess, isPlatformAdmin })}
        replace
      />
    );
  }

  return <>{children}</>;
}

function AccessContextError({
  message,
  onSignOut,
}: {
  message: string;
  onSignOut: () => Promise<void>;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold text-foreground">Unable to load account access</h1>
          <p className="text-sm text-muted-foreground">{message}</p>
          <p className="text-sm text-muted-foreground">
            Refresh and try again. If it continues, sign out and sign back in.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={() => window.location.reload()}>
            Refresh
          </Button>
          <Button onClick={() => void onSignOut()}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
